import { ZodError, ZodTypeAny, z } from 'zod';
import { locationParser } from '../store/location';
import { reservationParser } from '../store/reservation';
import { userParser } from '../store/user';
import { phpDateString } from './phpTypes';
import { zodPhpArrayObject } from './utils';
import { notificationParser } from '../store/notification';

// Disable missing translation message as translations will be added later.
// We can add a toggle for this later when we have most translations.
// eslint-disable-next-line
const consoleError = console.error.bind(console);
// eslint-disable-next-line
console.error = (message, ...args) => {
	if (message.toString().includes('Error MISSING_TRANSLATION')) {
		return;
	}
	consoleError(message, ...args);
};

const configParser = z.object({
	ajaxUrl: z.string(),
	dates: z.object({
		start: z.string().transform((s) => new Date(s)),
		end: z.string().transform((s) => new Date(s)),
	}),
	default_GPS_enter: z.tuple([z.number(), z.number()]),
	logged_in_user: z.number(),
	elevated_user: z.boolean(),
	admin_user: z.boolean(),
	mandatory_events: zodPhpArrayObject(
		z.object({
			start: z.string().transform((s) => new Date(s)),
			end: z.string().transform((s) => new Date(s)),
			info: z.string(),
		}),
	),
	textDomain: z.string(),
	resa_notes_max_length: z.number(),
});

declare global {
	interface Window {
		FocusResaConfig: z.infer<typeof configParser>;
	}
}

export class FocusResa {
	private static _instance: FocusResa;
	public static getInstance(): FocusResa {
		if (FocusResa._instance == null) {
			FocusResa._instance = new FocusResa();
		}
		return FocusResa._instance;
	}

	private config?: z.infer<typeof configParser>;
	private constructor() {
		try {
			this.config = configParser.parse(window.FocusResaConfig);
		} catch (e) {
			console.error('at window.FocusResaConfig', e.issues);
			throw new Error('[FocusResa] Configuration error');
		}
	}

	public get dates(): { start: Date; end: Date } {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.dates;
	}

	public get defaultGPSCenter(): [number, number] {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.default_GPS_enter;
	}

	public get mandatoryEvents() {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.mandatory_events;
	}

	public get textDomain() {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.textDomain;
	}

	public get loggedInUser(): number {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		const id = this.config.logged_in_user;
		if (id == null) return 0;
		return id;
	}

	public get isElevatedUser(): boolean {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.elevated_user;
	}

	public get isAdmin(): boolean {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.admin_user;
	}

	public get notesMaxLength(): number {
		if (!this.config) throw new Error('[FocusResa] No configuration');
		return this.config.resa_notes_max_length;
	}

	// level 0 debounce: do not call if request already running
	private _requests: Map<string, Promise<Response>> = new Map();
	private async ajax<T extends ZodTypeAny>(
		action: string,
		params: any,
		parser?: T,
	): Promise<T extends undefined ? unknown : z.infer<T>> {
		if (!this.config) throw new Error('FocusResa not configured');

		const data = {
			action: `focusresa_${action}`,
			...this.toUrlParameters(params),
		};

		const requestConfig = JSON.stringify({
			action,
			params,
		});

		const pResponse = this._requests.has(requestConfig)
			? this._requests.get(requestConfig)!
			: fetch(this.config.ajaxUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
						'Cache-Control': 'no-cache',
					},
					body: new URLSearchParams(data),
				}).then(async (r) => {
					setTimeout(() => {
						// throttle same request for 5s
						this._requests.delete(requestConfig);
					}, 5000);
					return r.json();
				});
		this._requests.set(requestConfig, pResponse);

		const ret = await pResponse;
		if (typeof ret !== 'object' || !('success' in ret))
			throw new Error(`AJAX Error: ${ret}`);
		if (!ret.success) throw new Error(`Api error ${JSON.stringify(ret.data)}`);

		if (parser != null) {
			const parsed = parser.safeParse(ret.data);
			if (!parsed.success) {
				const drillUnions = (e: ZodError, level: number = 0) => {
					for (const suberr of e.errors) {
						// console.error(requestConfig, ret.data, parsed.error.issues);
						if (suberr.code === 'invalid_union') {
							if (level === 0) console.group('Possible UnionError source');
							suberr.unionErrors.forEach((e) => {
								e.issues.forEach((i) =>
									console.debug(
										new Array(level).join(' '),
										['parsed', ...i.path].join('.'),
										':',
										i.message,
									),
								);
								drillUnions(e, level + 1);
							});
							if (level === 0) console.groupEnd();
						}
					}
				};
				drillUnions(parsed.error);
				throw new Error(`Parse error: ${parsed.error.toString()}`);
			}
			return parsed.data;
		}

		return ret.data ?? true;
	}

	public async getLocations() {
		return await this.ajax(
			'get_locations',
			{},
			zodPhpArrayObject(locationParser),
		);
	}
	public async getLocation(params: { id: number }) {
		return this.ajax('get_location', params, locationParser);
	}
	public async getParticipants() {
		return this.ajax('get_participants', {}, zodPhpArrayObject(userParser));
	}
	public async getParticipant(params: { id: string }) {
		return this.ajax('get_participant', params, userParser);
	}
	public async createReservation(params: {
		locationId: number;
		userIds: number[];
		dateFrom: Date;
		dateTo: Date;
		notes: string;
	}) {
		return await this.ajax(
			'create_reservation',
			{
				...params,
				dateFrom: phpDateString(params.dateFrom),
				dateTo: phpDateString(params.dateTo),
			},
			z.object({ id: z.number() }),
		);
	}
	public async updateReservation(params: {
		resaId: string;
		locationId?: number;
		dateFrom?: Date;
		dateTo?: Date;
		addUsers?: number[];
		removeUsers?: number[];
		notes?: string;
	}) {
		console.log(params);
		return await this.ajax('update_reservation', {
			...params,
			dateFrom: params.dateFrom ? phpDateString(params.dateFrom) : undefined,
			dateTo: params.dateTo ? phpDateString(params.dateTo) : undefined,
		});
	}
	public async getUserReservations(params: { userId: number }) {
		return this.ajax(
			'get_user_reservations',
			params,
			zodPhpArrayObject(reservationParser),
		);
	}
	public async getLocationReservations(params: { locationId: number }) {
		return this.ajax(
			'get_location_reservations',
			params,
			zodPhpArrayObject(reservationParser),
		);
	}

	public async cancelReservation(params: { resaId: number }) {
		return this.ajax('cancel_reservation', params);
	}

	public async acceptInvite(params: { resaId: number }) {
		return this.ajax('accept_invitation', params);
	}

	public async getNotifications(params: {
		userId: number;
		limit?: number;
		offset?: number;
	}) {
		return this.ajax(
			'get_notifications',
			params,
			zodPhpArrayObject(notificationParser),
		);
	}

	public async approveReservation(params: { resaId: number }) {
		return this.ajax('approve_reservation', params);
	}

	toUrlParameters(o: unknown): Record<string, string> {
		if (typeof o === 'object' && o)
			return Object.entries(o).reduce(
				(o, [k, v]) => ({ ...o, [k]: `${v}` }),
				{},
			);
		return {};
	}
}
